import {
  AloftPrice,
  CraftsmanCostProps,
  PositiveInt,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectImprovementEntityIds,
  selectImprovementsEntities,
} from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

// Bathrooms costs are included in the dwelling cost for SFR and Manufactured homes.
// This should only be used in multifamily residences for units with >1 bathroom (not applicable for MVP).
const calculateHalfBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];

  const identification = propertyIdentification[id];

  if (isUndefined(improvementsForTransaction) || isUndefined(identification)) {
    return undefined;
  }

  const {
    address: { PostalCode: zipCode },
  } = identification;
  const {
    halfBaths = 0 as PositiveInt,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = improvementsForTransaction;

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode)) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getHalfBathroomsCost({
    halfBaths,
    quality,
    propertyType,
    zipCode,
  });
};

const getTotalHalfBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const costOutput = calculateHalfBathCost(costProps, improvements, propertyIdentification, id);

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return costOutput.totalCost;
};

const getBaseHalfBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const costOutput = calculateHalfBathCost(costProps, improvements, propertyIdentification, id);

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return costOutput.multiplierBreakdown.none;
};

export const selectHalfBathCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalHalfBathCost,
);

export const selectBaseHalfBathCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getBaseHalfBathCost,
);

export const selectHalfBathCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) =>
    improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalHalfBathCost(costProps, improvements, propertyIdentification, propID);
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectSubjectHalfBathCostOutput = createSelector(
  [selectCostProps, selectImprovementsEntities, selectPropertyIdentificationEntities],
  (costProps, improvements, propertyIdentification) =>
    calculateHalfBathCost(costProps, improvements, propertyIdentification, 'subject'),
);

export const selectSubjectArvHalfBathCostOutput = createSelector(
  [selectCostProps, selectImprovementsEntities, selectPropertyIdentificationEntities],
  (costProps, improvements, propertyIdentification) =>
    calculateHalfBathCost(costProps, improvements, propertyIdentification, 'subjectArv'),
);
